import PropTypes from "prop-types";
import React, { useEffect } from "react";
import { useDispatch } from "react-redux";
import { makeStyles } from "@material-ui/core/styles";
import useHubUsers from "src/utils/hubs/HubUser";
import MesaLeaseBridge from "src/components/mesaLease/MesaLeaseBridge";
import useBackgroundSyncDetect from "../DashboardLayout/UseBackgroundSyncDetect";
import TpvRealtimeHubs from "src/realtime/TpvRealtimeHubs";
import useUserSessionHub from "src/utils/hubs/PrivateUserSessionHub";
import useAddOnGrantsHub from "src/utils/hubs/useAddOnGrantsHub";
import useDeviceEnforcementHub from "src/utils/hubs/useDeviceEnforcementHub";
import useInactivity from "src/hooks/useInactivity";
import KioskOverlay from "../DashboardLayout/KioskOverlay";
import { inactivityLockSeconds, shouldLockOnInactivity } from "src/utils/inactivityLock";
import { lockOnInactivityIfEnabled } from "src/utils/inactivityLockFlow";
import useAuth from "src/hooks/useAuth";
import { useSelector } from "src/redux/store";
import TpvThemeOverride from "src/theme/TpvThemeOverride";
import DeviceCommandsBridge from "src/components/DeviceCommandsBridge";
import useHubWorkerStatus from "src/utils/hubs/HubWorkerStatus";
import TpvStatusPill from "src/realtime/TpvStatusPill";

// ----------------------------------------------------------------------

const useStyles = makeStyles((theme) => ({
  root: {
    [theme.breakpoints.up("sm")]: {
      display: "flex",
      minHeight: "100%",
      overflow: "hidden",
    },
    [theme.breakpoints.down("sm")]: {
      height: "100%",
    },
  },
  main: {
    [theme.breakpoints.down("sm")]: {
      height: "100%",
    },
    [theme.breakpoints.up("sm")]: {
      flexGrow: 1,
      overflow: "auto",
      minHeight: "100%",
    },
  },
}));

// ----------------------------------------------------------------------

TpvCartaLayout.propTypes = {
  children: PropTypes.node,
};

function TpvCartaLayout({ children }) {
  useBackgroundSyncDetect();
  const dispatch = useDispatch();
  const { user } = useAuth();
  const { currentActiveEmployee, kioskOpen } = useSelector((state) => state.employees);
  // Nota: el "modo etiquetado" es PERSISTENTE — el camarero decide
  // cuándo entrar/salir. Antes lo reseteábamos al cambiar de mesa,
  // pero interfería con el flujo natural de trabajar mesa a mesa con
  // cursos. Ahora solo se apaga al pulsar Cocinar (CartWidget) o el
  // botón X del panel de etiquetas.

  // 2026-10-18 · Sin `|| 60` por defecto: ajuste apagado/ausente = nunca
  // bloquear (ver src/utils/inactivityLock.js).
  const isInactivity = useInactivity(inactivityLockSeconds(user));

  useEffect(() => {
    if (
      shouldLockOnInactivity({
        user,
        isInactive: isInactivity,
        kioskOpen,
        currentActiveEmployee,
      })
    ) {
      dispatch(lockOnInactivityIfEnabled());
    }
  }, [isInactivity, user, currentActiveEmployee, kioskOpen]);

  const classes = useStyles();

  useHubUsers();
  // Fase 4 · mesas, reservas, pedidos y productos: `<TpvRealtimeHubs />`
  // (una conexión `/hub/tpv` con V2, o los hubs legacy con el kill-switch).
  useUserSessionHub();
  // 2026-08-27 · Grants admin de add-ons — necesario también en TPV
  // porque el owner puede pasar toda la sesión en TpvCartaLayout (sin
  // TopBar/AlertsCenter) y sin este hook no recibía el push del admin
  // cuando le regalaban/retiraban un extra. HubConnectionManager es
  // refcount, así que compartir con AlertsCenter no crea 2 sockets.
  useAddOnGrantsHub();
  // 2026-10-06 · Dentro de una mesa (carta, producto, cobro) también hay que
  // oír «vuelve a la selección de empleado» (el empleado entró en otro
  // dispositivo): antes solo lo escuchaba AlertsCenter (barra del listado) y
  // el TPV no se enteraba hasta su siguiente petición.
  useDeviceEnforcementHub();
  // 2026-10-08 · Estado del worker de impresión también dentro de la mesa:
  // la pastilla de estado lo enseña y sin el hub se quedaría con el último
  // valor visto en el listado (mismo socket compartido por refcount).
  useHubWorkerStatus();

  return (
    <TpvThemeOverride>
      <div className={classes.root}>
        {kioskOpen && <KioskOverlay />}
        {/* Fase 1 TPV · lease de mesa (quién está dentro) */}
        <MesaLeaseBridge />
        {/* Admin › Versiones: órdenes remotas (limpiar caché) también dentro de
            una mesa; la limpieza espera a que se cierre un cobro abierto. */}
        <DeviceCommandsBridge />
        {/* Fase 4 · tiempo real del TPV (V2 `/hub/tpv` o legacy). */}
        <TpvRealtimeHubs />

        <div className={classes.main}>{children}</div>
        {/* Estado del TPV sin TopBar: la carta y el cobro la llevan en su
            cabecera; en el resto de pantallas, pastilla flotante pequeña
            arriba a la derecha (se oculta sola si hay una en cabecera). */}
        <TpvStatusPill variant="floating" />
        {/* Sin «?» de ayuda: aquí ya se está dentro de una mesa (carta,
            producto, cobro…) y estorba; solo sale en el listado (DashboardLayout). */}
      </div>
    </TpvThemeOverride>
  );
}

export default TpvCartaLayout;
